import { UserPayload } from "../models/SiteModel";

const URLAPI = process.env.REACT_APP_BASE_URL;

const buildApiUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = (URLAPI ?? "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/api/") && base.toLowerCase().endsWith("/api")
    ? path.substring(4)
    : path;

  return `${base}${normalizedPath.startsWith("/") ? "" : "/"}${normalizedPath}`;
};

const createHttpError = (response: Response, message = response.statusText) => {
  return new Error(message || `HTTP ${response.status}`, {
    cause: { status: response.status, message },
  });
};

export async function fetchGetProductosParaVenta(id_local: string | undefined, user: UserPayload, signal?: AbortSignal) {
  const token = user.token;
   const dataToPost = {
        id_local: id_local,
    };
  const response = await fetch(
    `${URLAPI}/Producto/GetProductosParaVenta?id_local=${id_local}`,
    {
      method: "POST",
      signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify(dataToPost),
    }
  );

  if (!response.ok) {
    throw createHttpError(response);
  }

  const data = await response.json();
  return data;
}

export type CatalogoClienteProductos = {
  idLocal: number;
  productos: any[];
};

export async function fetchGetCatalogoCliente(user: UserPayload, signal?: AbortSignal): Promise<CatalogoClienteProductos> {
  const response = await fetch(`${URLAPI}/Producto/CatalogoCliente`, {
    method: "GET",
    signal,
    headers: {
      "Authorization": "Bearer " + user.token
    },
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch { }
    throw createHttpError(response, message);
  }

  const data = await response.json();
  return {
    idLocal: Number(data.idLocal ?? data.IdLocal ?? 0),
    productos: data.productos ?? data.Productos ?? [],
  };
}

export async function fetchProductoCatalogoClienteImagen(user: UserPayload, imagenUrl: string, signal?: AbortSignal): Promise<string | undefined> {
  if (!imagenUrl) {
    return undefined;
  }

  const response = await fetch(buildApiUrl(imagenUrl), {
    method: "GET",
    signal,
    headers: {
      "Authorization": "Bearer " + user.token
    },
  });

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw createHttpError(response);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export type ProductosAdministracionPage = {
  items: any[];
  total: number;
  page: number;
  pageSize: number;
  activePriceFields: string[];
  headers: Record<string, string>;
};

export type ProductoCatalogoItem = {
  id: number;
  nombre: string;
  idPadre?: number;
};

export type ProductoPrecioMasivoItem = {
  idProducto: number;
  codigo: string;
  producto: string;
  rubro: string;
  subrubro: string;
  proveedor: string;
  costo: number;
  contado: number;
  mayor: number;
  menor: number;
  margenContado: number;
  margenMayor: number;
  margenMenor: number;
  ultimaModificacion: string;
  costoNuevo?: number;
  contadoNuevo?: number;
  mayorNuevo?: number;
  menorNuevo?: number;
  margenContadoNuevo?: number;
  margenMayorNuevo?: number;
  margenMenorNuevo?: number;
};

export type ProductoPrecioMasivoPage = {
  items: ProductoPrecioMasivoItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type ProductoPrecioMasivoRequest = {
  idLocal: number;
  idProductos: number[];
  aplicarATodoLoFiltrado: boolean;
  search?: string;
  idRubro?: number;
  idSubrubro?: number;
  idProveedor?: number;
  tipoModificacion: "%" | "$$" | "Z";
  aumentar: boolean;
  valor: number;
  modificarCompra: boolean;
  modificarContado: boolean;
  modificarMayor: boolean;
  modificarMenor: boolean;
  modificarMargenContado: boolean;
  modificarMargenMayor: boolean;
  modificarMargenMenor: boolean;
  noModificarPrecioVenta: boolean;
  redondearSinDecimales: boolean;
  ajustarDecimal99: boolean;
};

export async function fetchGetProductosAdministracion(
  idLocal: string | undefined,
  user: UserPayload,
  page: number,
  pageSize: number,
  search?: string,
  signal?: AbortSignal
): Promise<ProductosAdministracionPage> {
  const token = user.token;
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (search?.trim()) {
    params.set("search", search.trim());
  }

  const response = await fetch(
    `${URLAPI}/Producto/Administracion/${idLocal}?${params.toString()}`,
    {
      method: "GET",
      signal,
      headers: {
        'Authorization': 'Bearer ' + token
      },
    }
  );

  if (!response.ok) {
    throw createHttpError(response);
  }

  const data = await response.json();
  return {
    items: data.items ?? data.Items ?? [],
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
    activePriceFields: data.activePriceFields ?? data.ActivePriceFields ?? [],
    headers: data.headers ?? data.Headers ?? {},
  };
}

export async function fetchGetCatalogosModificacionPrecios(user: UserPayload, idRubro?: number, signal?: AbortSignal) {
  const params = new URLSearchParams();
  if (idRubro) {
    params.set("idRubro", String(idRubro));
  }
  const response = await fetch(`${URLAPI}/Producto/ModificacionPrecios/Catalogos?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { 'Authorization': 'Bearer ' + user.token },
  });

  if (!response.ok) {
    throw createHttpError(response);
  }

  const data = await response.json();
  return {
    rubros: data.rubros ?? data.Rubros ?? [],
    subrubros: data.subrubros ?? data.Subrubros ?? [],
    proveedores: data.proveedores ?? data.Proveedores ?? [],
  } as {
    rubros: ProductoCatalogoItem[];
    subrubros: ProductoCatalogoItem[];
    proveedores: ProductoCatalogoItem[];
  };
}

export async function fetchGetProductosModificacionPrecios(
  idLocal: string | undefined,
  user: UserPayload,
  page: number,
  pageSize: number,
  filters: { search?: string; idRubro?: number; idSubrubro?: number; idProveedor?: number },
  signal?: AbortSignal
): Promise<ProductoPrecioMasivoPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.idRubro) params.set("idRubro", String(filters.idRubro));
  if (filters.idSubrubro) params.set("idSubrubro", String(filters.idSubrubro));
  if (filters.idProveedor) params.set("idProveedor", String(filters.idProveedor));

  const response = await fetch(`${URLAPI}/Producto/ModificacionPrecios/${idLocal}?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { 'Authorization': 'Bearer ' + user.token },
  });

  if (!response.ok) {
    throw createHttpError(response);
  }

  const data = await response.json();
  return {
    items: data.items ?? data.Items ?? [],
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
  };
}

export async function fetchPreviewModificacionPrecios(user: UserPayload, request: ProductoPrecioMasivoRequest) {
  const response = await fetch(`${URLAPI}/Producto/ModificacionPrecios/Preview`, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + user.token
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw createHttpError(response);
  }

  const data = await response.json();
  return {
    items: data.items ?? data.Items ?? [],
    total: data.total ?? data.Total ?? 0,
  } as { items: ProductoPrecioMasivoItem[]; total: number };
}

export async function fetchAplicarModificacionPrecios(user: UserPayload, request: ProductoPrecioMasivoRequest) {
  const response = await fetch(`${URLAPI}/Producto/ModificacionPrecios/Aplicar`, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + user.token
    },
    body: JSON.stringify(request),
  });

  const data = await response.json();
  if (!response.ok) {
    throw createHttpError(response, data?.message ?? response.statusText);
  }

  return data;
}

export async function fetchGetProductoDetalle(user: UserPayload, idLocal:string|undefined, idProducto:string, signal?: AbortSignal) {
  const token = user?.token;
  const dataToPost = {
        id_local: idLocal,
        id_producto: idProducto
    };
  const response = await fetch(
    `${URLAPI}/Producto/GetProductoDetalle`,
    {
      method: "POST",
      signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify(dataToPost),
    }
  );

  if (!response.ok) {
    throw createHttpError(response);
  }
  
  const data = await response.json();
  return data;
}

export async function fetchGetProductoDetalleAdministracion(user: UserPayload, idLocal: string | undefined, idProducto: string, signal?: AbortSignal) {
  const token = user?.token;
  const response = await fetch(
    `${URLAPI}/Producto/Administracion/${idLocal}/Detalle/${idProducto}`,
    {
      method: "GET",
      signal,
      headers: {
        'Authorization': 'Bearer ' + token
      },
    }
  );

  if (!response.ok) {
    throw createHttpError(response);
  }

  return await response.json();
}

export async function fetchUpdateProductoPrecios(
  user: UserPayload,
  data: {
    id_producto: number;
    id_local?: number;
    precio_contado: number;
    precio_mayor: number;
    precio_menor: number;
  }
) {
  const token = user.token;
  const response = await fetch(
    `${URLAPI}/Producto/UpdateProductoPrecios`,
    {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw createHttpError(response);
  }

  const result = await response.json();
  return result;
}
